import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../components/Api";
import Loading from "../components/Loading";

function EditProduct() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    const [loading, setLoading] = useState(true);

    // Menus déroulants
    const [types, setTypes] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedSubtype, setSelectedSubtype] = useState(null);
    const [licenses, setLicenses] = useState([]);
    const [selectedLicenseCard, setSelectedLicenseCard] = useState("");
    const [languages, setLanguages] = useState([]);
    const [packagings, setPackagings] = useState([]);
    const [typeLinks, setTypeLinks] = useState([]);
    const [links, setLinks] = useState([{ link_url: "", type_link_id: "" }]);

    // Champs généraux
    const [form, setForm] = useState({
        name: "",
        batch: "",
        reference: "",
        weight: "",
        buy: "",
        sell: "",
        publication: "",
        brand: "",
        ean: "",
        dimension: "",
        language_id: "",
        packaging_id: "",
        commentary: "",
    });

    // Champs spécifiques
    const [figureFields, setFigureFields] = useState({ serie: "", personnage: "" });
    const [bookFields, setBookFields] = useState({ nbPages: "", couleur: "" });
    const [multimediaFields, setMultimediaFields] = useState({ typeMedia: "", code: "", artiste: "" });
    const [posterFields, setPosterFields] = useState({ serie: "", personnage: "" });
    const [goodieFields, setGoodieFields] = useState({ serie: "", personnage: "" });

    // Chargement des données nécessaires
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [typesRes, languagesRes, packagingsRes, typeLinksRes, licensesRes] = await Promise.all([
                    api.get("/types"),
                    api.get("/languages"),
                    api.get("/packaging"),
                    api.get("/type-links"),
                    api.get("/license-cards"),
                ]);
                setTypes(typesRes.data);
                setLanguages(languagesRes.data);
                setPackagings(packagingsRes.data);
                setTypeLinks(typeLinksRes.data);
                setLicenses(licensesRes.data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchData();
    }, []);

    // Charger le produit existant
    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/products/${id}`);
                const p = res.data;

                // Champs généraux
                setForm({
                    name: p.name || "",
                    batch: p.batch || "",
                    weight: p.weight || "",
                    buy: p.buy || "",
                    sell: p.sell || "",
                    publication: p.publication || "",
                    brand: p.brand || "",
                    ean: p.ean || "",
                    dimension: p.dimension || "",
                    language_id: p.language_id || "",
                    packaging_id: p.packaging_id || "",
                    commentary: p.commentary || "",
                });

                // Type / sous-type
                const type = types.find(t => t.id === p.type_id);
                if (type) {
                    setSelectedType({ ...type, subtypes: type.subtypes || [] });
                    const subtype = type.subtypes?.find(s => s.id === p.subtype_id);
                    setSelectedSubtype(subtype || null);
                }

                // License pour cartes
                if (p.type_id === 1 && p.cards?.length > 0) {
                    setSelectedLicenseCard(p.cards[0].license_card_id.toString());
                }

                // Champs spécifiques
                switch (p.type_id) {
                    case 2:
                        setBookFields({ nbPages: p.number_of_pages || "", couleur: p.color || "" });
                        break;
                    case 3:
                        setFigureFields({ serie: p.serie || "", personnage: p.personnage || "" });
                        break;
                    case 4:
                        setMultimediaFields({ typeMedia: p.multimedia_type || "", code: p.code || "", artiste: p.artist || "" });
                        break;
                    case 5:
                        setPosterFields({ serie: p.serie || "", personnage: p.personnage || "" });
                        break;
                    case 6:
                        setGoodieFields({ serie: p.serie || "", personnage: p.personnage || "" });
                        break;
                    default:
                        break;
                }

                // Liens
                setLinks(
                    p.links?.map(l => ({ link_url: l.link_url, type_link_id: l.type_link_id?.toString() || "" })) || [{ link_url: "", type_link_id: "" }]
                );

            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (types.length > 0) fetchProduct();
    }, [id, types]);

    if (loading) return <Loading message="Chargement..." />;

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

    const handleTypeChange = e => {
        const type = types.find(t => t.id === parseInt(e.target.value));
        setSelectedType({ ...type, subtypes: type?.subtypes || [] });
        setSelectedSubtype(null);
        setSelectedLicenseCard(""); // réinitialise license
    };

    const renderTypeSpecificForm = () => {
        if (!selectedType) return null;

        const fieldWrapper = (label, element) => (
            <div className="flex items-center justify-between">
                <label className="w-1/3 font-medium">{label} :</label>
                <div className="w-2/3">{element}</div>
            </div>
        );

        switch (selectedType.id) {
            case 1: // Cards
                return (
                    <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                        {fieldWrapper(
                            "Licence",
                            <select
                                value={selectedLicenseCard}
                                onChange={e => setSelectedLicenseCard(e.target.value)}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            >
                                <option value="">-- Licence --</option>
                                {licenses.map(l => <option key={l.id} value={l.id}>{l.license_card}</option>)}
                            </select>
                        )}
                    </div>
                );
            case 2: // Books
                return (
                    <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                        {fieldWrapper(
                            "Nombre de pages",
                            <input
                                type="number"
                                placeholder="Nombre de pages"
                                value={bookFields.nbPages}
                                onChange={e => setBookFields({ ...bookFields, nbPages: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                        {fieldWrapper(
                            "Couleur",
                            <input
                                type="text"
                                placeholder="Couleur"
                                value={bookFields.couleur}
                                onChange={e => setBookFields({ ...bookFields, couleur: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                    </div>
                );
            case 3: // Figures
                return (
                    <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                        {fieldWrapper(
                            "Série",
                            <input
                                type="text"
                                placeholder="Série"
                                value={figureFields.serie}
                                onChange={e => setFigureFields({ ...figureFields, serie: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                        {fieldWrapper(
                            "Personnage",
                            <input
                                type="text"
                                placeholder="Personnage"
                                value={figureFields.personnage}
                                onChange={e => setFigureFields({ ...figureFields, personnage: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                    </div>
                );
            case 4: // Multimedia
                return (
                    <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                        {fieldWrapper(
                            "Code",
                            <input
                                type="text"
                                placeholder="Code"
                                value={multimediaFields.code}
                                onChange={e => setMultimediaFields({ ...multimediaFields, code: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                        {fieldWrapper(
                            "Artiste",
                            <input
                                type="text"
                                placeholder="Artiste"
                                value={multimediaFields.artiste}
                                onChange={e => setMultimediaFields({ ...multimediaFields, artiste: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                    </div>
                );
            case 5: // Posters
                return (
                    <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                        {fieldWrapper(
                            "Série",
                            <input
                                type="text"
                                placeholder="Série"
                                value={posterFields.serie}
                                onChange={e => setPosterFields({ ...posterFields, serie: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                        {fieldWrapper(
                            "Personnage",
                            <input
                                type="text"
                                placeholder="Personnage"
                                value={posterFields.personnage}
                                onChange={e => setPosterFields({ ...posterFields, personnage: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                    </div>
                );
            case 6: // Goodies
                return (
                    <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                        {fieldWrapper(
                            "Série",
                            <input
                                type="text"
                                placeholder="Série"
                                value={goodieFields.serie}
                                onChange={e => setGoodieFields({ ...goodieFields, serie: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                        {fieldWrapper(
                            "Personnage",
                            <input
                                type="text"
                                placeholder="Personnage"
                                value={goodieFields.personnage}
                                onChange={e => setGoodieFields({ ...goodieFields, personnage: e.target.value })}
                                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                            />
                        )}
                    </div>
                );
            default:
                return null;
        }
    };


    const handleSubmit = async e => {
        e.preventDefault();

        try {
            // Gestion de license_card pour les cartes
            let licenseCardId = null;
            if (selectedType?.id === 1 && selectedLicenseCard) {
                licenseCardId = parseInt(selectedLicenseCard);
            }

            const payload = {
                ...form,
                type_id: selectedType?.id || null,
                subtype_id: selectedSubtype?.id || null,
                license_card_id: licenseCardId,
                links: links
                    .filter(l => l.link_url.trim() !== "")
                    .map(l => ({
                        link_url: l.link_url.trim(),
                        type_link_id: l.type_link_id ? parseInt(l.type_link_id) : null
                    })),
                brand: form.brand || null,
                ean: form.ean || null,
                dimension: form.dimension || null,
                commentary: form.commentary || null,
                publication: form.publication || null,
                packaging_id: form.packaging_id ? parseInt(form.packaging_id) : null,
                buy: form.buy ? parseFloat(form.buy) : null,
                sell: form.sell ? parseFloat(form.sell) : null,
            };

            // Champs spécifiques selon le type
            if (selectedType) {
                switch (selectedType.id) {
                    case 2: // Books
                        payload.number_of_pages = bookFields.nbPages ? parseInt(bookFields.nbPages) : null;
                        payload.color = bookFields.couleur || null;
                        break;
                    case 3: // Figures
                        payload.serie = figureFields.serie || null;
                        payload.personnage = figureFields.personnage || null;
                        break;
                    case 4: // Multimedia
                        payload.multimedia_type = multimediaFields.typeMedia || null;
                        payload.code = multimediaFields.code || null;
                        payload.artist = multimediaFields.artiste || null;
                        break;
                    case 5: // Posters
                        payload.serie = posterFields.serie || null;
                        payload.personnage = posterFields.personnage || null;
                        break;
                    case 6: // Goodies
                        payload.serie = goodieFields.serie || null;
                        payload.personnage = goodieFields.personnage || null;
                        break;
                    default:
                        break;
                }
            }

            const res = await api.put(`/products/${id}`, payload);
            navigate(`/product/${res.data.id}`);

        } catch (err) {
            if (err.response?.data?.errors) {
                alert("Erreur de validation : " + JSON.stringify(err.response.data.errors));
            } else {
                console.error(err);
                alert("Erreur lors de la mise à jour du produit");
            }
        }
    };

    return (
        <div className="container mx-auto p-4 space-y-6">
            <h1 className="text-2xl font-bold dark:text-white">Modifier le produit</h1>
            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Type / sous-type */}
                <div className="space-y-4 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                    <label>Type :</label>
                    <select value={selectedType?.id || ""} onChange={handleTypeChange} className="border p-1 w-full dark:bg-gray-700 bg-gray-050">
                        <option value="">-- Sélectionnez un type --</option>
                        {types.map(t => <option key={t.id} value={t.id}>{t.type_name}</option>)}
                    </select>

                    {selectedType?.subtypes?.length > 0 && (
                        <div>
                            <label>Sous-type :</label>
                            <select value={selectedSubtype?.id || ""} onChange={e => {
                                const s = selectedType.subtypes.find(s => s.id === parseInt(e.target.value));
                                setSelectedSubtype(s);
                            }} className="border p-1 w-full dark:bg-gray-700 bg-gray-050">
                                <option value="">-- Sélectionnez un sous-type --</option>
                                {selectedType.subtypes.map(s => <option key={s.id} value={s.id}>{s.subtype_name}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {renderTypeSpecificForm()}

                {/* Champs généraux */}
                <div className="p-6 border rounded bg-gray-100 dark:bg-gray-800 space-y-4">

                    <div className="flex items-center justify-between">
                        <label htmlFor="name" className="w-1/3 font-medium">Nom :</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            placeholder="Nom"
                            value={form.name}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <label htmlFor="batch" className="w-1/3 font-medium">Lot :</label>
                        <input
                            type="text"
                            id="batch"
                            name="batch"
                            placeholder="Lot"
                            value={form.batch}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <label htmlFor="weight" className="w-1/3 font-medium">Poids (g) :</label>
                        <input
                            type="number"
                            id="weight"
                            name="weight"
                            placeholder="Poids"
                            value={form.weight}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <label htmlFor="buy" className="w-1/3 font-medium">Prix d'achat (€) :</label>
                        <input
                            type="number"
                            id="buy"
                            name="buy"
                            placeholder="Prix d'achat"
                            value={form.buy}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <label htmlFor="sell" className="w-1/3 font-medium">Prix de revente (€) :</label>
                        <input
                            type="number"
                            id="sell"
                            name="sell"
                            placeholder="Prix de revente"
                            value={form.sell}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    {/* Date de publication */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="publication" className="w-1/3 font-medium">Date de publication :</label>
                        <input
                            type="date"
                            id="publication"
                            name="publication"
                            value={form.publication}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    {/* Marque */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="brand" className="w-1/3 font-medium">Marque :</label>
                        <input
                            type="text"
                            id="brand"
                            name="brand"
                            placeholder="Marque"
                            value={form.brand}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    {/* EAN */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="ean" className="w-1/3 font-medium">EAN :</label>
                        <input
                            type="text"
                            id="ean"
                            name="ean"
                            placeholder="EAN"
                            value={form.ean}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    {/* Dimension */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="dimension" className="w-1/3 font-medium">Dimension :</label>
                        <input
                            type="text"
                            id="dimension"
                            name="dimension"
                            placeholder="Dimension"
                            value={form.dimension}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        />
                    </div>

                    {/* Langue */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="language_id" className="w-1/3 font-medium">Langue :</label>
                        <select
                            id="language_id"
                            name="language_id"
                            value={form.language_id}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        >
                            <option value="">-- Sélectionnez une langue --</option>
                            {languages.map(l => (
                                <option key={l.id} value={l.id}>{l.language_name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Packaging */}
                    <div className="flex items-center justify-between">
                        <label htmlFor="packaging_id" className="w-1/3 font-medium">Packaging pour envoi :</label>
                        <select
                            id="packaging_id"
                            name="packaging_id"
                            value={form.packaging_id}
                            onChange={handleChange}
                            className="w-2/3 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 bg-gray-050"
                        >
                            <option value="">-- Sélectionnez un packaging --</option>
                            {packagings.map(p => (
                                <option key={p.id} value={p.id}>{p.packaging_name}</option>
                            ))}
                        </select>
                    </div>
                </div>


                {/* Commentaire */}
                <div className="space-y-2 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                    <label name="commentary">Commentaire :</label>
                    <textarea name="commentary" value={form.commentary} onChange={handleChange} className="border p-1 w-full dark:bg-gray-700 bg-gray-050" rows="4"></textarea>
                </div>

                {/* Liens */}
                <div className="space-y-2 p-4 border rounded bg-gray-100 dark:bg-gray-800">
                    <h2 className="font-semibold">Liens</h2>
                    {links.map((link, index) => (
                        <div key={index} className="flex space-x-2 items-center">
                            <input
                                type="text"
                                placeholder="URL"
                                value={link.link_url}
                                onChange={e => {
                                    const newLinks = [...links];
                                    newLinks[index].link_url = e.target.value;
                                    setLinks(newLinks);
                                }}
                                className="border p-1 flex-1 dark:bg-gray-700 bg-gray-050"
                            />
                            <select
                                value={link.type_link_id}
                                onChange={e => {
                                    const newLinks = [...links];
                                    newLinks[index].type_link_id = e.target.value;
                                    setLinks(newLinks);
                                }}
                                className="border p-1 dark:bg-gray-700 bg-gray-050"
                            >
                                <option value="">-- Type de lien --</option>
                                {typeLinks.map(tl => <option key={tl.id} value={tl.id}>{tl.type_link_name}</option>)}
                            </select>
                            <button type="button" onClick={() => setLinks(links.filter((_, i) => i !== index))} className="bg-red-500 text-white px-2 py-1 rounded">
                                Supprimer
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setLinks([...links, { link_url: "", type_link_id: "" }])} className="mt-2 bg-green-500 text-white px-4 py-2 rounded">
                        Ajouter un lien
                    </button>
                </div>

                <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">Enregistrer</button>
            </form>
        </div>
    );
}

export default EditProduct;